import React from 'react';
import { Box, InputBase } from '@mui/material';

export interface CodeFieldProps {
  /** The digits typed so far, e.g. "4821". */
  value: string;
  onChange: (code: string) => void;
  /** Called once every box is filled, typed or pasted: submit from here. */
  onComplete?: (code: string) => void;
  /** How many digits. 6 by default. */
  length?: number;
  disabled?: boolean;
  /** Draws the boxes as wrong, e.g. after a code is refused. */
  error?: boolean;
  autoFocus?: boolean;
  /** Names the field, e.g. "Verification code". */
  ariaLabel?: string;
}

/**
 * A one-time code, one box per digit: the code someone was emailed to prove
 * an address is theirs. Typing moves to the next box, Backspace to the one
 * before, and pasting the whole code fills every box at once; the phone
 * offers the code from the message where it can (`one-time-code`).
 *
 * For any other number, use `NumberField`.
 */
export function CodeField({
  value, onChange, onComplete, length = 6, disabled, error, autoFocus, ariaLabel = 'Verification code',
}: CodeFieldProps) {
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  React.useEffect(() => {
    if (autoFocus) refs.current[Math.min(value.length, length - 1)]?.focus();
    // Only on mount: refocusing on every change would fight the person typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (next: string, focusAt: number) => {
    const code = next.replace(/\D/g, '').slice(0, length);
    onChange(code);
    refs.current[Math.min(focusAt, length - 1)]?.focus();
    if (code.length === length) onComplete?.(code);
  };

  const typed = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    const chars = digits.slice();
    chars[index] = digit;
    set(chars.join(''), index + 1);
  };

  const keyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key !== 'Backspace') return;
    e.preventDefault();
    const chars = digits.slice();
    if (chars[index]) {
      chars[index] = '';
      set(chars.join(''), index);
    } else if (index > 0) {
      chars[index - 1] = '';
      set(chars.join(''), index - 1);
    }
  };

  const pasted = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const code = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (code) set(code, code.length);
  };

  return (
    <Box role="group" aria-label={ariaLabel} sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {digits.map((digit, i) => (
        <InputBase
          // The boxes never reorder: the index is the identity.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          value={digit}
          disabled={disabled}
          inputRef={(el: HTMLInputElement | null) => { refs.current[i] = el; }}
          onChange={(e) => typed(i, e.target.value)}
          onKeyDown={(e) => keyDown(i, e)}
          onPaste={pasted}
          onFocus={(e) => e.target.select()}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: i === 0 ? 'one-time-code' : 'off',
            'aria-label': `Digit ${i + 1} of ${length}`,
            maxLength: 1,
          }}
          sx={(t) => ({
            ...t.typography.h3,
            fontVariantNumeric: 'tabular-nums',
            width: t.ds.sizes.control.lg + 4,
            height: t.ds.sizes.control.lg + 12,
            borderRadius: `${t.ds.radius.md}px`,
            border: `1px solid ${error ? t.tokens.semantic.danger
              : digit ? t.tokens.brand.primary : t.tokens.border.default}`,
            bgcolor: digit ? t.tokens.brand.primarySofter : t.tokens.surface.raised,
            transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
            '&.Mui-focused': { borderColor: t.tokens.border.focus, boxShadow: `0 0 0 3px ${t.tokens.brand.primarySoft}` },
            '& input': { textAlign: 'center', p: 0 },
          })}
        />
      ))}
    </Box>
  );
}
